import { useEffect, useMemo, useState } from "react";
import { getAdminProfile } from "../../features/adminAuth/adminAuthStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const INITIAL_FORM = {
  subject: "",
  title: "",
  preheader: "",
  intro: "",
  body: "",
  ctaText: "",
  ctaUrl: "",
  footerNote: "",
  singleEmail: "",
};

const EmailManagementPage = () => {
  const profile = getAdminProfile();

  const [recipients, setRecipients] = useState([]);
  const [totals, setTotals] = useState({ total: 0, active: 0, suspended: 0 });
  const [mode, setMode] = useState("bulk");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sendReport, setSendReport] = useState(null);

  const loadRecipients = async () => {
    setLoadingRecipients(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/emails/recipients`);
      const payload = await response.json();

      if (!response.ok) {
        setError(payload.message || "Failed to load recipients.");
        return;
      }

      setRecipients(Array.isArray(payload.data) ? payload.data : []);
      setTotals(payload.totals || { total: 0, active: 0, suspended: 0 });
    } catch (_error) {
      setError("Unable to connect to backend service.");
    } finally {
      setLoadingRecipients(false);
    }
  };

  useEffect(() => {
    loadRecipients();
  }, []);

  const activeRecipients = useMemo(
    () => recipients.filter((recipient) => recipient.status !== "suspended"),
    [recipients]
  );

  const resolvedRecipientCount = useMemo(() => {
    if (mode === "single") {
      return form.singleEmail.trim() ? 1 : 0;
    }

    if (selectedEmails.length > 0) {
      return selectedEmails.length;
    }

    return recipients.length;
  }, [form.singleEmail, mode, recipients.length, selectedEmails.length]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSelectedEmail = (email) => {
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter((item) => item !== email) : [...prev, email]
    );
  };

  const handleSelectActive = () => {
    setSelectedEmails(activeRecipients.map((item) => item.email));
  };

  const handleClearSelection = () => {
    setSelectedEmails([]);
  };

  const handleSend = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setSendReport(null);

    if (!form.subject.trim()) {
      setError("Subject is required.");
      return;
    }

    if (!form.title.trim()) {
      setError("Email title is required.");
      return;
    }

    if (!form.intro.trim()) {
      setError("Email intro is required.");
      return;
    }

    if (!form.body.trim()) {
      setError("Email body is required.");
      return;
    }

    if (mode === "single" && !form.singleEmail.trim()) {
      setError("Recipient email is required in single mode.");
      return;
    }

    setSending(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/emails/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mode,
          email: form.singleEmail,
          selectedEmails,
          subject: form.subject,
          title: form.title,
          preheader: form.preheader,
          intro: form.intro,
          body: form.body,
          ctaText: form.ctaText,
          ctaUrl: form.ctaUrl,
          footerNote: form.footerNote,
          senderName: profile?.name || profile?.email || "Admin",
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        setError(payload.message || "Failed to send campaign.");
        return;
      }

      setSuccess(payload.message || "Campaign sent successfully.");
      setSendReport(payload.data || null);
    } catch (_error) {
      setError("Unable to connect to backend service.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="admin-page-card email-page">
      <div className="email-page-head">
        <div>
          <h2>Email Management</h2>
          <p>Send beautiful campaign emails to one user or your entire user base with a branded template.</p>
        </div>
        <button type="button" className="row-btn row-btn-muted" onClick={loadRecipients} disabled={loadingRecipients}>
          {loadingRecipients ? "Refreshing..." : "Refresh Users"}
        </button>
      </div>

      <div className="email-summary-grid">
        <article className="email-summary-card">
          <span>Total Users</span>
          <strong>{totals.total}</strong>
        </article>
        <article className="email-summary-card">
          <span>Active</span>
          <strong>{totals.active}</strong>
        </article>
        <article className="email-summary-card">
          <span>Suspended</span>
          <strong>{totals.suspended}</strong>
        </article>
        <article className="email-summary-card email-summary-card-accent">
          <span>Recipients in Current Send</span>
          <strong>{resolvedRecipientCount}</strong>
        </article>
      </div>

      {error ? <p className="auth-error">{error}</p> : null}
      {success ? <p className="auth-success">{success}</p> : null}

      <form className="email-composer" onSubmit={handleSend}>
        <div className="email-composer-grid">
          <label>
            Send Mode
            <select value={mode} onChange={(event) => setMode(event.target.value)}>
              <option value="bulk">Bulk Send</option>
              <option value="single">Single Send</option>
            </select>
          </label>

          {mode === "single" ? (
            <label>
              Recipient Email
              <input
                type="email"
                value={form.singleEmail}
                onChange={(event) => updateForm("singleEmail", event.target.value)}
                placeholder="user@example.com"
                required
              />
            </label>
          ) : (
            <div className="email-selection-controls">
              <span>Bulk Audience</span>
              <div>
                <button type="button" className="row-btn row-btn-muted" onClick={handleSelectActive}>Select Active</button>
                <button type="button" className="row-btn row-btn-muted" onClick={handleClearSelection}>Clear Selection</button>
              </div>
            </div>
          )}

          <label>
            Subject
            <input
              type="text"
              value={form.subject}
              onChange={(event) => updateForm("subject", event.target.value)}
              placeholder="Important Verixxon Update"
              required
            />
          </label>

          <label>
            Header Title
            <input
              type="text"
              value={form.title}
              onChange={(event) => updateForm("title", event.target.value)}
              placeholder="Your Account Update"
              required
            />
          </label>

          <label>
            Preheader (optional)
            <input
              type="text"
              value={form.preheader}
              onChange={(event) => updateForm("preheader", event.target.value)}
              placeholder="Short inbox preview line"
            />
          </label>

          <label>
            Intro Text
            <input
              type="text"
              value={form.intro}
              onChange={(event) => updateForm("intro", event.target.value)}
              placeholder="We are reaching out with an important update"
              required
            />
          </label>

          <label className="email-composer-wide">
            Message Body
            <textarea
              rows={8}
              value={form.body}
              onChange={(event) => updateForm("body", event.target.value)}
              placeholder="Write your message. New lines are rendered as clean paragraphs."
              required
            />
          </label>

          <label>
            CTA Label (optional)
            <input
              type="text"
              value={form.ctaText}
              onChange={(event) => updateForm("ctaText", event.target.value)}
              placeholder="Go to Dashboard"
            />
          </label>

          <label>
            CTA URL (optional)
            <input
              type="url"
              value={form.ctaUrl}
              onChange={(event) => updateForm("ctaUrl", event.target.value)}
              placeholder="https://verixxon.com"
            />
          </label>

          <label className="email-composer-wide">
            Footer Note (optional)
            <input
              type="text"
              value={form.footerNote}
              onChange={(event) => updateForm("footerNote", event.target.value)}
              placeholder="Why recipients are receiving this email"
            />
          </label>
        </div>

        <div className="email-composer-actions">
          <button type="submit" disabled={sending}>
            {sending ? "Sending Campaign..." : "Send Campaign"}
          </button>
        </div>
      </form>

      {mode === "bulk" ? (
        <section className="email-recipient-panel">
          <h3>Recipient Selection</h3>
          <p>Select specific users or leave empty to send to all users.</p>

          <div className="email-recipient-list">
            {recipients.map((recipient) => {
              const checked = selectedEmails.includes(recipient.email);
              return (
                <label key={recipient.id || recipient.email} className="email-recipient-row">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelectedEmail(recipient.email)}
                  />
                  <span className="email-recipient-email">{recipient.email}</span>
                  <span
                    className={
                      recipient.status === "suspended"
                        ? "status-badge status-suspended"
                        : "status-badge status-active"
                    }
                  >
                    {recipient.status || "active"}
                  </span>
                </label>
              );
            })}
          </div>
        </section>
      ) : null}

      {sendReport ? (
        <section className="email-report-panel">
          <h3>Last Campaign Report</h3>
          <p>
            Sent: <strong>{sendReport.sentCount}</strong> | Failed: <strong>{sendReport.failedCount}</strong> | Total: <strong>{sendReport.totalRecipients}</strong>
          </p>
        </section>
      ) : null}
    </section>
  );
};

export default EmailManagementPage;
